import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type PendingNavigation = {
  data?: any;
  unused?: string;
  url?: string | URL | null;
};

export const useUnsavedChangesWarning = (condition: boolean) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const originalPushState = useRef<typeof history.pushState>();
  const navigate = useNavigate();

  const executePendingNavigation = useCallback(() => {
    if (!pendingNavigation || !originalPushState.current) return;

    const { data, unused, url } = pendingNavigation;
    originalPushState.current(data, unused || '', url);

    if (url) {
      const path = typeof url === 'string' ? url : url.toString();
      navigate(path);
    }

    setPendingNavigation(null);
  }, [pendingNavigation, navigate]);

  const handleNavigation = useCallback(
    (confirm: boolean) => {
      setShowModal(false);
      if (confirm) {
        executePendingNavigation();
      } else {
        setPendingNavigation(null);
      }
    },
    [executePendingNavigation],
  );

  useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (condition) {
        e.preventDefault();
        return 'You have unsaved changes. Do you want to leave the page?';
      }
    };

    if (condition) {
      window.addEventListener('beforeunload', beforeUnloadHandler);

      if (!originalPushState.current) {
        originalPushState.current = history.pushState.bind(history);
      }

      const interceptPushState = (data: any, unused: string, url?: string | URL | null) => {
        if (condition) {
          setPendingNavigation({ data, unused, url });
          setShowModal(true);
          return;
        }
        originalPushState.current!(data, unused, url);
      };

      history.pushState = interceptPushState;
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      if (originalPushState.current) {
        history.pushState = originalPushState.current;
      }
    };
  }, [condition]);

  return {
    showModal,
    confirmNavigation: () => handleNavigation(true),
    cancelNavigation: () => handleNavigation(false),
  };
};
